import {
    getLoopFacets,
    getProductFilter,
    getSortBy,
    setFacetSelections,
} from '~/features/ProductList/utils';
import { UmbracoTypes } from '~/lib/data-contract';
import {
    AttributeFilterParameter,
    GebaAttributeNameValuePair,
    OrFilterParameter,
} from '~/lib/data-contract/auto-generated/loop54-api-model';
import {
    EntitiesByAttributeRequest,
    EntitiesRequest,
    FilterDefaultsResponse,
    SortOptions,
} from '~/services/loop54/types';

// Sort options that are invalid for non-indexed entity requests
const NONINDEXED_INVALID_SORTS = ['relevance'];

interface GetFilterQueryAttributes {
    breadcrumb?: UmbracoTypes.IBreadcrumb;
    displayCategoryPath?: boolean;
    filterAdmin?: UmbracoTypes.ProductListPageProperties['filterAdmin'];
    filterDefaults?: FilterDefaultsResponse;
    filtersFromUrl: Record<string, string[]>;
    pageNumber: number;
    pageTitle: string | null | undefined;
    productsPerPage?: number;
}

export const getFilterQuery = ({
    breadcrumb,
    displayCategoryPath,
    filterAdmin,
    filterDefaults,
    filtersFromUrl,
    pageNumber,
    pageTitle,
    productsPerPage = 30,
}: GetFilterQueryAttributes) => {
    const {
        defaultFacets,
        facets: allFacets,
        defaultSortOptions,
        sortOptions: allSortOptions,
    } = filterDefaults || {};

    // Use page configured filters (hidden/static)
    const filters = getFiltersWithValues(filterAdmin?.filters ?? {});
    const loopFilters = mapFiltersToLoopFilters(filters);

    const loopFacets = getLoopFacets(allFacets, defaultFacets, filterAdmin, filters);

    if (displayCategoryPath) loopFacets.push({ attributeName: 'CategoryPath', type: 'distinct' });

    setFacetSelections(loopFacets, filtersFromUrl);

    const indexedFilterAttribute = findAndRemoveIndexedFilterAttribute(filters);
    if (!indexedFilterAttribute) {
        removeInvalidSortings(allSortOptions, defaultSortOptions, filterAdmin?.sortOptions);
    }

    const selectedContext = filtersFromUrl?.ContextGroup?.[0];
    const typeAndContextFilter = getProductFilter();

    // Select sort from URL, or fall back to default from page config
    // then first from page config
    // then first of defaults (or 'popularity' if NextJS doing SSR)
    const sortParameter =
        filtersFromUrl?.sort?.[0] ||
        filterAdmin?.sort ||
        filterAdmin?.sortOptions?.[0] ||
        Object.values(defaultSortOptions ?? {})?.[0]?.key ||
        'popularity';

    const sortBy = getSortBy(allSortOptions, sortParameter);

    const customData = selectedContext ? { selectedContext } : undefined;

    const result: EntitiesByAttributeRequest | EntitiesRequest = {
        attribute: indexedFilterAttribute as GebaAttributeNameValuePair,
        customData,
        resultsOptions: {
            facets: loopFacets,
            filter: {
                and: [typeAndContextFilter, ...loopFilters],
            },
            sortBy,
            take: pageNumber * productsPerPage,
        },
        ...(indexedFilterAttribute && {
            requestAlias: {
                name: 'Subcategory',
                value: pageTitle ?? '',
                details: breadcrumb?.items?.map((item) => item.title).join('|') ?? '',
            },
        }),
    };

    if (!result.customData) {
        delete result.customData;
    }

    if ('attribute' in result && !result.attribute) {
        // @ts-ignore - irrelevant ts check
        delete result.attribute;
    }

    return result;
};

/**
 * Finds and removes the first filter (if any) that is for an indexed attribute.
 * This indexed attribute can then used with the GetEntitiesByAttribute endpoint.
 */
const findAndRemoveIndexedFilterAttribute = (filters: Record<string, string[]>) => {
    // Find first filter on an indexed attribute (used to determine GetEntities/GetEntitiesByAttribute endpoint)
    const indexedAttributes = ['ChannelNodeCode', 'Brand', 'SubBrand'];
    const indexedFilterAttributeKey = Object.keys(filters).find((key) =>
        indexedAttributes.includes(key),
    );

    if (!indexedFilterAttributeKey) {
        return undefined;
    }

    // @ts-ignore - TODO: Loop54 needs to update their model to allow this.
    const indexedFilterAttribute = {
        name: indexedFilterAttributeKey,
        value: filters[indexedFilterAttributeKey].join('|'),
    } as GebaAttributeNameValuePair;

    delete filters[indexedFilterAttributeKey];

    return indexedFilterAttribute;
};

const getFiltersWithValues = (pageFilters: Record<string, string[]>) => {
    // Filters out any page filters that is either undefined/null or empty array
    return Object.fromEntries(
        Object.entries(pageFilters).filter(([, values]) => values?.length),
    ) as Record<string, string[]>;
};

const mapFiltersToLoopFilters = (filters: Record<string, string[]>): OrFilterParameter[] => {
    // Map filters to Loop model (OR-filter of each value for each filter)
    return Object.entries(filters ?? {}).map(([key, values]) => ({
        or: values.map(
            (value): AttributeFilterParameter => ({
                type: 'attribute',
                attributeName: key,
                value: value,
            }),
        ),
    }));
};

const removeInvalidSortings = (
    allSortOptions: SortOptions | undefined,
    defaultSortOptions: SortOptions | undefined,
    pageSortOptions: string[] | undefined,
) => {
    if (pageSortOptions) {
        pageSortOptions = pageSortOptions.filter(
            (sort) => !NONINDEXED_INVALID_SORTS.includes(sort),
        );
    }

    for (const sort of NONINDEXED_INVALID_SORTS) {
        delete allSortOptions?.[sort];
        delete defaultSortOptions?.[sort];
    }
};
